import type { components } from '@adiox/api';

export type ChallengeDetail = components['schemas']['ChallengeDetail'];
export type ChallengeRecord = components['schemas']['ChallengeRecord'];
export type RuleProgress = components['schemas']['RuleProgress'];
export type PayoutDto = components['schemas']['PayoutDto'];

export type Tone = 'ok' | 'warn' | 'danger';

export interface Meter {
  id: 'target' | 'daily' | 'max' | 'days';
  label: string;
  /** 0 to 100, how much of the bar is filled. */
  percent: number;
  tone: Tone;
  /** The figures behind the bar, in words. */
  text: string;
  /** What the bar is a share of, for assistive technology. */
  valueText: string;
}

const money = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const clamp = (v: number) => Math.max(0, Math.min(100, v));

/** A loss meter goes amber at 50 % of the limit and red at 80 %: the same levels the server warns at. */
export function lossTone(percent: number): Tone {
  return percent >= 80 ? 'danger' : percent >= 50 ? 'warn' : 'ok';
}

export function meters(p: RuleProgress, funded: boolean, currency: string): Meter[] {
  const result: Meter[] = [];
  if (!funded) {
    const percent = p.profitTarget > 0 ? clamp((p.profit / p.profitTarget) * 100) : 0;
    result.push({
      id: 'target',
      label: 'Profit target',
      percent,
      tone: 'ok',
      text: `${money(p.profit)} of ${money(p.profitTarget)} ${currency}${p.targetMet ? ' (target reached)' : ''}`,
      valueText: `${percent.toFixed(0)} percent of the profit target`,
    });
  }

  const daily = clamp(p.dailyLossUsedPercent);
  result.push({
    id: 'daily',
    label: 'Daily loss',
    percent: daily,
    tone: lossTone(daily),
    text: `${money(p.dailyLossUsed)} of ${money(p.dailyLossLimit)} ${currency} used, ${money(Math.max(0, p.dailyLossLimit - p.dailyLossUsed))} left today (floor ${money(p.dailyLossFloor)})`,
    valueText: `${daily.toFixed(0)} percent of the daily loss limit used`,
  });

  const max = clamp(p.maxLossUsedPercent);
  result.push({
    id: 'max',
    label: 'Maximum loss',
    percent: max,
    tone: lossTone(max),
    text: `${money(p.maxLossUsed)} of ${money(p.maxLossLimit)} ${currency} used, ${money(Math.max(0, p.maxLossLimit - p.maxLossUsed))} left (floor ${money(p.maxLossFloor)})`,
    valueText: `${max.toFixed(0)} percent of the maximum loss limit used`,
  });

  if (!funded) {
    const needed = p.minTradingDays;
    const done = needed > 0 ? clamp((p.tradingDays / needed) * 100) : 100;
    result.push({
      id: 'days',
      label: 'Trading days',
      percent: done,
      tone: 'ok',
      text:
        needed > 0
          ? `${p.tradingDays} of ${needed} needed${p.daysLeft !== null && p.daysLeft !== undefined ? ` · ${p.daysLeft} day${p.daysLeft === 1 ? '' : 's'} left in this phase` : ''}`
          : `${p.tradingDays} so far, none required${p.daysLeft !== null && p.daysLeft !== undefined ? ` · ${p.daysLeft} day${p.daysLeft === 1 ? '' : 's'} left in this phase` : ''}`,
      valueText: needed > 0 ? `${p.tradingDays} of ${needed} trading days` : `${p.tradingDays} trading days`,
    });
  }
  return result;
}

export interface Headline {
  tone: Tone;
  mark: string;
  title: string;
  detail: string;
}

/** The one thing to say about the challenge right now, in words (never only a colour). */
export function headline(d: ChallengeDetail): Headline {
  const c = d.current.challenge;
  const funded = d.rules.kind === 'Funded';
  switch (c.status) {
    case 'Failed':
      return {
        tone: 'danger',
        mark: '✕',
        title: funded ? 'Funded account closed by a rule' : `${d.current.stageName} failed`,
        detail: c.breachText ?? 'A rule was broken. Positions were closed and trading is switched off.',
      };
    case 'Passed':
      return {
        tone: 'ok',
        mark: '✓',
        title: `${d.current.stageName} passed`,
        detail:
          'Your profit target is met and the account is settled. The next stage is opened for you by your account manager.',
      };
    case 'Closed':
      return {
        tone: 'warn',
        mark: '–',
        title: 'This account is closed',
        detail: 'It is kept for your history. Use your current account to keep trading.',
      };
    default:
      return {
        tone: 'ok',
        mark: '●',
        title: funded ? 'Funded account' : `${d.current.stageName} in progress`,
        detail: funded
          ? `You keep ${d.rules.profitSplitPercent}% of the profit. The loss limits below still apply.`
          : `Reach the profit target without breaking a loss limit.${d.rules.minTradingDays > 0 ? ` You also need ${d.rules.minTradingDays} trading days.` : ''}`,
      };
  }
}

/** The rules as sentences, so the client reads the same definitions the server enforces. */
export function ruleSentences(d: ChallengeDetail): string[] {
  const r = d.rules;
  const lines = [
    `Daily loss: your equity may not fall ${r.maxDailyLossPercent}% of the starting balance below the higher of your balance and equity at server midnight.`,
    `Maximum loss: your equity may not fall ${r.maxTotalLossPercent}% of the starting balance below the starting balance. This floor never moves up.`,
  ];
  if (r.kind === 'Challenge') {
    lines.unshift(
      `Profit target: ${r.profitTargetPercent}% of the starting balance, with no open positions when it is reached.`,
    );
    if (r.minTradingDays > 0)
      lines.push(`Trading days: at least ${r.minTradingDays} server days with a trade.`);
    if (r.maxDays) lines.push(`Time limit: ${r.maxDays} server days for this phase.`);
  } else {
    lines.push(`Profit split: ${r.profitSplitPercent}% of the profit is yours.`);
  }
  lines.push(
    'Days start at server midnight. Breaking a loss limit closes your positions at once and switches trading off.',
  );
  return lines;
}

/** A message when the payout amount cannot be requested, otherwise null (the server checks again). */
export function payoutProblem(amountText: string, payable: number): string | null {
  const amount = Number(amountText);
  if (amountText.trim() === '' || !Number.isFinite(amount) || amount <= 0)
    return 'Enter the amount as a positive number.';
  if (amount > payable) return `The most you can request now is ${money(payable)}.`;
  return null;
}

/** How much can still be lost before each loss limit is broken (never negative), and how much of each is used. */
export interface LossRoom {
  daily: number;
  max: number;
  dailyUsedPercent: number;
  maxUsedPercent: number;
}

export const lossRoom = (p: RuleProgress): LossRoom => ({
  daily: Math.max(0, p.dailyLossLimit - p.dailyLossUsed),
  max: Math.max(0, p.maxLossLimit - p.maxLossUsed),
  dailyUsedPercent: clamp(p.dailyLossUsedPercent),
  maxUsedPercent: clamp(p.maxLossUsedPercent),
});

/**
 * A warning when losing `risk` (the money lost if the stop loss is hit, as a positive amount) would break a loss limit, or
 * null. The tighter of the two limits is named. The server decides the breach; this only warns before sending.
 */
export function riskWarning(risk: number, room: LossRoom, currency: string): string | null {
  if (!(risk > 0)) return null;
  const [name, left] =
    room.daily <= room.max ? ['daily loss limit', room.daily] : ['maximum loss limit', room.max];
  if (risk <= left) return null;
  return `At the stop loss this trade loses about ${money(risk)} ${currency}, more than the ${money(left)} ${currency} left before the ${name}. Hitting it would fail the challenge.`;
}
